// Cloudflare Worker API client for Tarot Diary App
import { analytics } from './analytics';

// Use the environment variable as the single source of truth for the API URL.
// Fallback to the production URL if the variable is not set.
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://diary.tarotreadingcard.online';

const API_KEY = process.env.REACT_APP_API_KEY;

// Validate required environment variables
if (!API_KEY) {
  console.warn('REACT_APP_API_KEY is not set. Some API calls may require authentication.');
}

// Debug function to test AI API manually from console
window.testAIAPI = async (testData = null) => {
  const defaultTestData = {
    spreadName: "Test Reading",
    spreadType: "Single Card",
    question: "What should I focus on today?",
    cards: [
      {
        position: "Present",
        cardName: "The Fool",
        cardPosition: "upright",
        basicMeaning: "New beginnings, innocence, spontaneity"
      }
    ]
  };
  
  const spreadData = testData || defaultTestData;
  console.log('Testing AI API with data:', spreadData);
  
  try {
    const response = await api.generateAIExplanation(spreadData);
    console.log('AI API Success:', response);
    return response;
  } catch (error) {
    console.error('AI API Error:', error);
    throw error;
  }
};

console.log('Debug function added: window.testAIAPI() - call this to test AI API');

// Generate a simple session ID for the browser
const getSessionId = () => {
  let sessionId = localStorage.getItem('tarot-session-id');
  if (!sessionId) {
    sessionId = Math.random().toString(36).substring(2) + Date.now().toString(36);
    localStorage.setItem('tarot-session-id', sessionId);
  }
  return sessionId;
};

// Helper function for API calls
const apiCall = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint.startsWith('/api') ? '' : '/api'}${endpoint}`;
  
  // Determine if this endpoint requires an API key (exclude AI endpoints which use JWT auth only)
  const isAIEndpoint = endpoint.startsWith('/ai/');
  const requiresApiKey = options.method && options.method !== 'GET' && !isAIEndpoint;
  
  // Get JWT token if available for authenticated requests
  const authToken = localStorage.getItem('authToken');
  
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...(requiresApiKey && { 'X-API-Key': API_KEY }),
      ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
    },
    credentials: 'include', // Include credentials for CORS
    ...options,
  };

  try {
    console.log(`Making API call to: ${url}`);
    console.log('Request config (headers sanitized):', {
      ...config,
      headers: {
        ...config.headers,
        'X-API-Key': config.headers['X-API-Key'] ? '[HIDDEN]' : undefined,
        'Authorization': config.headers['Authorization'] ? '[HIDDEN]' : undefined
      }
    });
    
    const response = await fetch(url, config);
    console.log('Response status:', response.status);
    console.log('Response headers:', [...response.headers.entries()]);
    
    // Log rate limiting info if available
    const rateLimitRemaining = response.headers.get('X-RateLimit-Remaining');
    const rateLimitReset = response.headers.get('X-RateLimit-Reset');
    if (rateLimitRemaining !== null) {
      console.log(`Rate limit: ${rateLimitRemaining} requests remaining, resets at ${rateLimitReset}`);
    }
    
    // Get the raw response text for debugging
    const responseText = await response.text();
    console.log('Raw response text:', responseText);
    console.log('Response content type:', response.headers.get('content-type'));
    
    // Try to parse as JSON
    let data;
    try {
      data = JSON.parse(responseText);
      console.log('Parsed response data:', data);
    } catch (parseError) {
      console.error('Failed to parse response as JSON:', parseError);
      console.error('Raw response was:', responseText.substring(0, 500) + (responseText.length > 500 ? '...' : ''));
      throw new Error(`Invalid JSON response: ${parseError.message}. Raw response: ${responseText.substring(0, 200)}`);
    }
    
    if (!response.ok) {
      // If we get 401 and the error is about invalid token, clear the stored token
      if (response.status === 401 && (data.error === 'Invalid authentication token' || data.error === 'Authentication required')) {
        console.log('Clearing invalid authentication token');
        localStorage.removeItem('authToken');
        
        // Notify user they need to log in again
        if (data.error === 'Invalid authentication token') {
          console.warn('Your session has expired. Please log in again.');
        }
      }
      throw new Error(data.message || data.error || `HTTP error! status: ${response.status}`);
    }
    
    return data;
  } catch (error) {
    console.error('API Error details:', {
      url,
      config: {
        ...config,
        headers: {
          ...config.headers,
          'X-API-Key': config.headers['X-API-Key'] ? '[HIDDEN]' : undefined,
          'Authorization': config.headers['Authorization'] ? '[HIDDEN]' : undefined
        }
      },
      error: error.message,
      stack: error.stack
    });
    throw error;
  }
};

const api = {
  // Import reading from JWT token
  importReading: async (token) => {
    const result = await apiCall('/api/import', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
    
    // Track import reading event
    analytics.trackImportReading();
    
    return result;
  },

  // Get all readings for authenticated user
  getReadings: async () => {
    const authToken = localStorage.getItem('authToken');
    
    if (!authToken) {
      throw new Error('Authentication required to view readings. Please log in.');
    }
    
    const response = await apiCall('/diary/readings');
    
    // Track diary view
    analytics.trackViewDiary();
    
    return response.readings || [];
  },

  // Save a new reading (requires authentication)
  saveReading: async (readingData) => {
    const authToken = localStorage.getItem('authToken');
    console.log('saveReading - Auth token exists:', !!authToken);
    console.log('saveReading - Auth token length:', authToken ? authToken.length : 0);
    
    if (!authToken) {
      throw new Error('Authentication required to save readings to diary. Please log in.');
    }
    
    const result = await apiCall('/diary/readings', {
      method: 'POST',
      body: JSON.stringify(readingData),
    });
    
    // Track save reading event
    analytics.trackSaveReading(readingData.spreadType, result.reading?.id);
    
    return result;
  },

  // Generate AI-enhanced explanation for a tarot spread
  generateAIExplanation: async (spreadData) => {
    const authToken = localStorage.getItem('authToken');
    
    if (!authToken) {
      throw new Error('Authentication required to generate AI explanations. Please log in.');
    }
    
    const result = await apiCall('/ai/explain-spread', {
      method: 'POST',
      body: JSON.stringify(spreadData),
    });
    
    // Track AI explanation generation
    analytics.trackEvent('ai_explanation_generated', {
      spread_type: spreadData.spreadType,
      card_count: spreadData.cardCount
    });
    
    return result;
  },

  // Delete reading (requires authentication)
  deleteReading: async (readingId) => {
    const authToken = localStorage.getItem('authToken');
    
    if (!authToken) {
      throw new Error('Authentication required to delete readings. Please log in.');
    }
    
    const result = await apiCall(`/diary/readings/${readingId}`, {
      method: 'DELETE',
    });
    
    // Track delete reading event
    analytics.trackDeleteReading(readingId);
    
    return result;
  },

  // Get reading statistics (requires authentication)
  getStats: async () => {
    const authToken = localStorage.getItem('authToken');
    
    if (!authToken) {
      throw new Error('Authentication required to view statistics. Please log in.');
    }
    
    const response = await apiCall('/diary/stats');
    return response.stats || {};
  },

  // Get user readings (authenticated users only)
  getUserReadings: async (limit = 50, offset = 0) => {
    const authToken = localStorage.getItem('authToken');
    
    if (!authToken) {
      throw new Error('User must be authenticated to get user readings');
    }
    
    const response = await apiCall(`/diary/readings?limit=${limit}&offset=${offset}`);
    
    // Track diary view
    analytics.trackViewDiary();
    
    // Return the full response (includes readings and pagination)
    return response;
  },

  // Get user reading statistics (authenticated users only)  
  getUserStats: async () => {
    const authToken = localStorage.getItem('authToken');
    
    if (!authToken) {
      throw new Error('User must be authenticated to get user stats');
    }

    const response = await apiCall('/diary/stats');
    return response.stats || {};
  },

  // Update a reading (for AI explanations and other updates)
  updateReading: async (readingId, updateData) => {
    const authToken = localStorage.getItem('authToken');
    
    if (!authToken) {
      throw new Error('Authentication required to update readings. Please log in.');
    }

    // Convert field names from camelCase to snake_case for backend compatibility
    const backendData = {};
    if (updateData.aiExplanation !== undefined) {
      backendData.ai_explanation = updateData.aiExplanation;
    }
    if (updateData.notes !== undefined) {
      backendData.notes = updateData.notes;
    }
    if (updateData.question !== undefined) {
      backendData.question = updateData.question;
    }

    const result = await apiCall(`/diary/readings/${readingId}`, {
      method: 'PUT',
      body: JSON.stringify(backendData),
    });

    // Track reading update
    analytics.trackEvent('reading_updated', {
      reading_id: readingId,
      has_ai_explanation: !!updateData.aiExplanation
    });

    return result.reading || result;
  },

  // Health check
  healthCheck: async () => {
    return await apiCall('/');
  },

  // Export session ID for external use
  getSessionId,

  // Link current session readings to authenticated user
  linkSessionReadings: async () => {
    const sessionId = getSessionId();
    const authToken = localStorage.getItem('authToken');
    
    if (!authToken) {
      throw new Error('User must be authenticated to link session readings');
    }
    
    const result = await apiCall('/auth/link-session', {
      method: 'POST',
      body: JSON.stringify({ sessionId }),
    });
    
    // Track session linking event
    analytics.trackEvent('session_linked', {
      session_id: sessionId,
      linked_readings: result.linkedCount || 0
    });
    
    return result;
  },
};

export default api;
